import { cn } from '@invent/wl-ui-kit-next';
import { Badge, Tooltip, TooltipContent, TooltipTrigger } from '@invent/wl-ui-kit-next/ui';
import { CircleAlert, CircleCheck, CircleDashed, ExternalLink, Info } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { VeritantWordmark } from '@/components/veritant-wordmark';
import {
  PROVENANCE_LABEL,
  PROVENANCE_NOTE,
  SCREEN_STATUS_LABEL,
  SUPPORT_ADDRESS,
  type Provenance,
  type ScreenMark,
  type ScreenStatus
} from './fixtures';
import type { IRefusal, IWireCall } from './flow';

/**
 * The chrome every screen of the onboarding study sits in.
 *
 * The screens themselves are drawn as the respondent would see them — same brand shell as
 * `login-page.tsx`, same card, same type — and everything that explains them is kept
 * *outside* that shell. A mock-up that mixes its own commentary into the screen is a
 * mock-up nobody can judge the screen from. The one thing a screen does carry about itself is
 * whether it is the product: the strip above it, and a `Not built` tag on the half of a
 * screen the product lacks.
 */

/**
 * Whether the study's own chrome is showing.
 *
 * Default **off**, and that is the point of it: with the rails, the tabs, the provider
 * switch and a note under every paragraph, the screen being reviewed is the smallest thing
 * on the page. Closed, the page is what a respondent would see, marked as a mock-up; open, it
 * is the review tool. One control switches between them, so nothing has to be judged through
 * the furniture around it.
 */
const ChromeContext = React.createContext(false);

export function StudyChrome({
  open,
  children
}: {
  open: boolean;
  children: React.ReactNode;
}) {
  return <ChromeContext.Provider value={open}>{children}</ChromeContext.Provider>;
}

/**
 * Read by anything that is commentary rather than screen — `Notes`, above all. Not to decide
 * whether `MockStatusStrip` or an inline `Not built` tag renders: saying what is a mock-up is
 * the commentary both modes keep.
 */
export function useStudyChrome(): boolean {
  return React.useContext(ChromeContext);
}

const PROVENANCE_TONE: Record<Provenance, string> = {
  shipped: 'border-transparent bg-emerald-100 text-emerald-900',
  stubbed: 'border-transparent bg-amber-100 text-amber-900',
  'not-built': 'border-transparent bg-rose-100 text-rose-900'
};

const PROVENANCE_ICON: Record<Provenance, React.ComponentType<{ className?: string }>> = {
  shipped: CircleCheck,
  stubbed: CircleAlert,
  'not-built': CircleDashed
};

/**
 * @param note What the tooltip says instead of the provenance's own definition — for a tag
 *   whose "not built" is this app's half rather than the server's, where "no route answers
 *   this" would be false.
 */
export function ProvenanceBadge({
  provenance,
  note,
  className
}: {
  provenance: Provenance;
  note?: string;
  className?: string;
}) {
  const Icon = PROVENANCE_ICON[provenance];
  return (
    <Tooltip>
      {/* A button rather than a span: this tooltip carries the only definition of "Stubbed
          provider" anywhere on the page, and a hover-only trigger keeps the study's central
          legend from a keyboard. It diverges from team-portal's SourceBadge, which has the
          same gap and should follow. */}
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
            PROVENANCE_TONE[provenance],
            className
          )}
        >
          <Icon className="size-3" aria-hidden />
          {PROVENANCE_LABEL[provenance]}
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm">{note ?? PROVENANCE_NOTE[provenance]}</TooltipContent>
    </Tooltip>
  );
}

/** The strip borrows the badge's palette, so green, amber and rose mean one thing on the page. */
const STATUS_PROVENANCE: Record<ScreenStatus, Provenance> = {
  live: 'shipped',
  stubbed: 'stubbed',
  'not-built': 'not-built'
};

/**
 * What the screen below is a mock-up of — commentary that stays with the chrome closed, like
 * the `Not built` tags on a half-built screen and for the same reason.
 *
 * Everything else that explains a screen hides behind the pill, because the first question
 * asked of a prototype is about the screen. This answers a different one, "is this the
 * product?", and the mode that hides the notes is exactly the one a screenshot is taken in: a
 * sign-up form over invented data, unmarked, is something somebody will eventually mistake
 * for the real thing. So it is deliberately **not** gated on `useStudyChrome()`, and it sits
 * outside the card, where no screen's own copy can be confused with it.
 *
 * Rendered by `FlowScreen` rather than by `ScreenShell`: three screens — the activation
 * message, the landing and the empty catalogue — draw their own layout, and a strip that each
 * shell had to carry would be missing from exactly those.
 */
export function MockStatusStrip({ mark }: { mark: ScreenMark }) {
  const provenance = STATUS_PROVENANCE[mark.status];
  const Icon = PROVENANCE_ICON[provenance];
  // Read for layout, not to decide whether to render: with the chrome closed the pills float
  // over the top of the page, and a strip that starts under them is one nobody can read.
  const chrome = useStudyChrome();

  return (
    <div className={cn('bg-[var(--color-off-white)] px-6', chrome ? 'pt-6' : 'pt-14')}>
      <div
        role="note"
        className={cn(
          'mx-auto flex max-w-2xl flex-wrap items-center gap-x-3 gap-y-1 rounded-md px-4 py-2',
          'text-sm',
          PROVENANCE_TONE[provenance]
        )}
      >
        <Icon className="size-4 shrink-0" aria-hidden />
        <span className="font-medium">{SCREEN_STATUS_LABEL[mark.status]}</span>
        {mark.status === 'live' ? (
          // A new tab: the run lives in this page's memory, and a reviewer comparing the two
          // should not have to walk the journey again to get back to where they were.
          <Link
            to={mark.route}
            target="_blank"
            rel="noreferrer"
            className="ml-auto inline-flex items-center gap-1 underline underline-offset-2"
          >
            The real screen: <span className="font-mono">{mark.route}</span>
            <ExternalLink className="size-3" aria-hidden />
            <span className="sr-only">(opens in a new tab)</span>
          </Link>
        ) : (
          <span className="basis-full pl-7 text-xs">{mark.missing}</span>
        )}
      </div>
    </div>
  );
}

/**
 * The anonymous-surface shell: off-white field, wordmark, one card.
 *
 * Copied in shape from `login-page.tsx` and `reset-password-page.tsx` rather than invented,
 * because a registration screen that does not look like the sign-in screen beside it is
 * answering a question nobody asked.
 */
export function ScreenShell({
  title,
  lede,
  children,
  footer
}: {
  title: string;
  lede?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <div className="flex justify-center bg-[var(--color-off-white)] px-6 py-10">
      <div className="w-full max-w-md">
        <VeritantWordmark tone="sapphire" className="-ml-[0.33em] mb-8 text-3xl" />

        <div className="rounded-lg border border-border bg-card p-8 shadow-sm">
          <h1 className="text-2xl text-[var(--color-royal-blue)]">{title}</h1>
          {lede && <div className="mt-2 mb-6 text-sm text-muted-foreground">{lede}</div>}
          {children}
        </div>

        {footer && <div className="mt-6 text-center text-sm text-muted-foreground">{footer}</div>}
      </div>
    </div>
  );
}

/**
 * Where the run stands, in the server's own three steps — and **study chrome**, not screen.
 *
 * `anchor` / `confirm_identity` / `complete` is the vocabulary a client switches on, and a
 * reviewer comparing a screen to the API contract should be able to see which step it is
 * without reading the code. A respondent should see none of it, so with the chrome closed
 * the ribbon is absent rather than translated: whether the real flow shows a progress
 * indicator at all, and in what words, is a decision this study has no business taking by
 * leaving a placeholder on the screen.
 */
export function StepRibbon({
  steps,
  current
}: {
  steps: { code: string; label: string }[];
  current: string;
}) {
  if (!useStudyChrome()) return null;

  const currentIndex = steps.findIndex((entry) => entry.code === current);

  return (
    <ol className="mb-6 flex flex-wrap items-center gap-2 text-xs">
      {steps.map((step, index) => {
        const active = step.code === current;
        const done = currentIndex > index;
        return (
          <li key={step.code} className="flex items-center gap-2">
            <span
              className={cn(
                'rounded-full border px-2 py-0.5 font-medium',
                active && 'border-transparent bg-[var(--color-royal-blue)] text-white',
                done && 'border-transparent bg-[var(--color-slight-blue)] text-[var(--color-royal-blue)]',
                !active && !done && 'text-muted-foreground'
              )}
            >
              {step.label}
            </span>
            {index < steps.length - 1 && <span className="text-muted-foreground">→</span>}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * A note about the screen above it — a decision it encodes, or a thing it must not do.
 *
 * These are the review's actual content. They are deliberately quiet: a reviewer looks at
 * the screen first and reads the note when the screen raises a question.
 */
export function Annotation({
  title,
  children,
  tone = 'neutral'
}: {
  title: string;
  children: React.ReactNode;
  tone?: 'neutral' | 'gap';
}) {
  return (
    <div
      className={cn(
        'rounded-md border-l-2 bg-card/60 px-3 py-2 text-xs leading-relaxed',
        tone === 'gap'
          ? 'border-l-[var(--color-magenta)]'
          : 'border-l-[var(--color-ocean-blue)]'
      )}
    >
      <p className="font-medium text-[var(--color-royal-blue)]">{title}</p>
      <div className="mt-1 text-muted-foreground">{children}</div>
    </div>
  );
}

/**
 * How to reach a human, on a screen that has just told somebody they cannot continue.
 *
 * The **address** does not exist in any form, so it renders as a blank to be filled rather
 * than as something plausible. The **reference** is the run's id, and printing it is safe for
 * the reason the refusal itself is careful: a run id is the one thing the registrant already
 * holds, it names nobody, and it is exactly what the staff queue is keyed on.
 *
 * It is the missing half of two screens that are otherwise the product's — the refusal of a
 * number, and the completion refusing an address — so it is marked `Not built` where it is
 * drawn rather than in the strip above, which describes the screen as a whole and would be
 * wrong about one half whichever it said. The refusal screen prints the reference itself, as
 * the product does, and passes none; the completion's product screen prints none, so the
 * block carries it there.
 */
export function ContactBlock({ reference, what }: { reference?: string; what: string }) {
  return (
    <div className="mt-6 rounded-md border bg-[var(--color-slight-blue)]/30 p-4 text-sm">
      <p
        className={cn(
          'flex items-center justify-between gap-2 font-medium',
          'text-[var(--color-royal-blue)]'
        )}
      >
        Talk to us
        <ProvenanceBadge provenance="not-built" />
      </p>
      <p className="mt-1 text-muted-foreground">{what}</p>

      <dl className="mt-3 grid gap-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <dt className="text-xs text-muted-foreground">Email</dt>
          <dd>
            {SUPPORT_ADDRESS ? (
              <a href={`mailto:${SUPPORT_ADDRESS}`} className="text-[var(--color-sapphire)]">
                {SUPPORT_ADDRESS}
              </a>
            ) : (
              <span className="rounded border border-dashed px-2 py-0.5 text-xs text-[var(--color-deep-magenta)]">
                address not supplied
              </span>
            )}
          </dd>
        </div>
        {reference && (
          <div className="flex flex-wrap items-baseline gap-2">
            <dt className="text-xs text-muted-foreground">Reference</dt>
            <dd className="font-mono text-xs">{reference.slice(0, 8)}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}

/** One request the real flow would make, rendered as the wire log's row. */
export function WireEntry({ call, index }: { call: IWireCall; index: number }) {
  const [open, setOpen] = React.useState(false);

  return (
    <li className="rounded-md border bg-card">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="grid w-full gap-1 px-3 py-2 text-left"
      >
        {/* Two lines rather than one: a run id makes the path 60 characters, and a path that
            has to share a row with three badges either truncates to uselessness or pushes
            them out of the column. */}
        <span className="flex flex-wrap items-center gap-2">
          <span className="w-4 shrink-0 text-xs text-muted-foreground">{index + 1}</span>
          <Badge variant="outline" className="font-mono text-[11px]">
            {call.method}
          </Badge>
          {/* A registration inside an account: the one thing on the log that says the run is
              bound, since the path and the body are the anonymous run's. */}
          {call.bearer && (
            <Badge variant="outline" className="font-mono text-[11px]">
              Bearer
            </Badge>
          )}
          <span
            className={cn(
              'rounded px-1.5 py-0.5 font-mono text-[11px]',
              call.status < 300
                ? 'bg-emerald-100 text-emerald-900'
                : 'bg-[var(--color-slight-blue)] text-[var(--color-deep-magenta)]'
            )}
          >
            {call.status}
          </span>
          <ProvenanceBadge provenance={call.provenance} className="ml-auto" />
        </span>
        <span className="pl-6 font-mono text-xs break-all">{call.path}</span>
      </button>

      {open && (
        <div className="grid gap-2 border-t px-3 py-2 text-xs">
          {call.request && <Payload label="Request" value={call.request} />}
          {call.response && <Payload label="Response" value={call.response} />}
          <p className="flex gap-1.5 text-muted-foreground">
            <Info className="mt-0.5 size-3 shrink-0" aria-hidden />
            <span>{call.note}</span>
          </p>
        </div>
      )}
    </li>
  );
}

function Payload({ label, value }: { label: string; value: unknown }) {
  return (
    <div>
      <p className="text-[11px] font-medium text-muted-foreground uppercase">{label}</p>
      <pre
        className={cn(
          'mt-1 overflow-x-auto rounded bg-[var(--color-slight-blue)]/40 p-2',
          'font-mono text-[11px] text-[var(--color-royal-blue)]'
        )}
      >
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

/**
 * "← Back", under the primary action and deliberately quiet.
 *
 * It is a link rather than a button because it is never what the screen is for: a registrant
 * who is on the right track should not have to read past it. Every screen that shows one has
 * a note saying what the step back costs on the server, which is not the same answer twice.
 */
export function BackLink({ label = 'Back', onClick }: { label?: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-5 justify-self-center text-sm text-muted-foreground hover:text-[var(--color-royal-blue)]"
    >
      ← {label}
    </button>
  );
}

/**
 * What a stale tab sees: the step the run is actually on, said on the screen rather than only
 * in the wire log.
 *
 * The 409 carries `step` and `status` precisely so a client can re-render instead of starting
 * over, and a study that logged the status code while leaving the card untouched would be
 * demonstrating the opposite of what those two members are for.
 */
export function OutOfStepNotice({ refusal }: { refusal: Nullable<IRefusal> }) {
  if (refusal?.reason !== 'registration_out_of_step') return null;

  return (
    <div
      role="status"
      className={cn(
        'mb-5 rounded-md border border-[var(--color-ocean-blue)]',
        'bg-[var(--color-slight-blue)]/40 px-3 py-2 text-sm'
      )}
    >
      <p className="font-medium text-[var(--color-royal-blue)]">This tab had fallen behind</p>
      <p className="mt-0.5 text-muted-foreground">
        Your registration has moved on since this page was loaded. {refusal.staffDetail} Nothing
        was lost — carry on from here.
      </p>
    </div>
  );
}

/** A big, obvious choice — what a radio group is for, drawn as something worth clicking. */
export function ChoiceButton({
  label,
  hint,
  onClick,
  selected,
  className
}: {
  label: string;
  hint?: string;
  onClick: () => void;
  selected?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full rounded-md border px-4 py-3 text-left text-sm transition-colors',
        selected
          ? 'border-[var(--color-sapphire)] bg-[var(--color-slight-blue)]/60'
          : 'border-border hover:border-[var(--color-ocean-blue)] ' +
              'hover:bg-[var(--color-slight-blue)]/30',
        className
      )}
    >
      <span className="font-medium text-[var(--color-royal-blue)]">{label}</span>
      {hint && <span className="mt-0.5 block text-xs text-muted-foreground">{hint}</span>}
    </button>
  );
}
